import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowDown } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1607014593352-adca51a032bd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxzdW5zaGluZSUyMGNvYXN0JTIwcXVlZW5zbGFuZCUyMG9jZWFufGVufDB8fHx8MTc4NTIzMzY2NHww&ixlib=rb-4.1.0&q=85";

const lines = ["Coastal cocktails,", "crafted where the", "ocean meets the sand."];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.3 } },
};
const line = {
  hidden: { y: "110%" },
  show: { y: 0, transition: { duration: 1, ease: [0.16, 1, 0.3, 1] } },
};

export const Hero = ({ onViewPackages }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.28]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.42, 0.7]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[640px] overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
        <img src={HERO_IMG} alt="Sunshine Coast ocean" className="w-full h-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-ocean-deep" />
      <div className="absolute inset-0 bg-gradient-to-t from-ocean-deep/70 via-transparent to-ocean-deep/30" />

      <div className="relative z-10 h-full max-w-7xl mx-auto px-5 md:px-8 flex flex-col justify-center">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.8 }}
          className="overline text-xs md:text-sm text-coral mb-6"
        >
          Sunshine Coast · Queensland · Mobile Bar
        </motion.p>

        <motion.h1
          variants={container}
          initial="hidden"
          animate="show"
          className="font-serif text-white text-5xl sm:text-6xl lg:text-8xl leading-[0.98] tracking-tight max-w-5xl"
        >
          {lines.map((l, i) => (
            <span key={i} className="reveal-mask">
              <motion.span variants={line} className="block">
                {l}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9 }}
          className="mt-8 text-white/85 text-base md:text-lg max-w-xl leading-relaxed"
        >
          One expert bartender. Endless coastal settings — beachfront weddings, backyard soirées,
          boat parties and corporate events from Noosa to Caloundra.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.9 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <button
            data-testid="hero-packages-btn"
            onClick={onViewPackages}
            className="rounded-full bg-coral px-8 py-4 text-white overline text-xs hover:bg-[#c9633f] transition-colors"
          >
            View Packages
          </button>
          <Link
            to="/quote"
            data-testid="hero-quote-btn"
            className="rounded-full border border-white/40 backdrop-blur-sm px-8 py-4 text-white overline text-xs hover:bg-white hover:text-ocean-deep transition-colors"
          >
            Get a Quote
          </Link>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/70 flex flex-col items-center gap-2"
      >
        <span className="overline text-[10px]">Scroll</span>
        <ArrowDown className="w-4 h-4 animate-bounce" />
      </motion.div>
    </section>
  );
};

export default Hero;
